import * as analytics from 'analytics/Analytics';
import * as benchlingUtil from 'BenchlingUtil';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import CreateProteinModal, {CreateProteinModalProps} from 'create-components/CreateAaSequenceModal';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import AppDispatcher from 'lib/Dispatcher';
import AddAnnotationToFeatureLibraryModal from 'molbio/feature-libraries/AddAnnotationToFeatureLibraryModal';
import {MatchType} from 'molbio/feature-libraries/FeatureLibraryConstants';
import {createFeature} from 'molbio/feature-libraries/featureLibraryUtil';
import {AA_SEQUENCE_IMPORT_MODAL_TAB} from 'molbio/MolBioConstants';
import notifications from 'notifications';
import preferences from 'Preferences';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import runModal from 'ui-library/runModal';
import {Optional} from 'util/Types';

import ProteinDAO, {ProteinCreateData, ProteinUpdateData} from './AaSequenceDAO';
import ProteinStore from './AaSequenceStore';
import {
  Protein,
  PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY,
  ProteinVersion,
} from './util/AaSequenceConstants';
import handleProteinUpdateError from './util/handleAaSequenceUpdateError';

const ProteinActions = {
  /**
   * Open a modal for the user to pick the folder, then make a new protein with the given amino
   * acids. If the user creates a new file this way, open it.
   */
  async promptCreateAndOpen(initialFolderId: Optional<string>): Promise<void> {
    const result = await runModal<CreateProteinModalProps>(CreateProteinModal, {
      title: 'Create AA sequence',
      initialFolderId: initialFolderId ?? undefined,
      oligoAwareFolderItemTypes: [ModelTypeConstants.PROTEIN],
      entityLinkFieldLabel: 'Translation link',
    });
    if (result != null) {
      this.createAndOpen({
        name: result.name,
        aminoAcids: '',
        folderId: result.folderId,
        tagSchemaId: result.tagSchemaId,
        studyIds: result.studyIds,
      });
    }
  },

  /**
   * Open a modal for the user to pick the folder, then make a new protein with the given amino
   * acids. If the user creates a new file this way, open it.
   */
  createAndOpen({
    name,
    aminoAcids,
    folderId,
    success,
    tagSchemaId,
    studyIds,
  }: {
    name: string;
    aminoAcids: string;
    folderId: string;
    success?: () => unknown;
    tagSchemaId?: string;
    studyIds?: Array<string>;
  }): void {
    const FileBrowserActions = require('file-browser/FileBrowserActions').default;

    LoadingScreen.show();
    ProteinActions.create({
      name,
      aminoAcids,
      folder: folderId,
      success(proteinId: string) {
        FileBrowserActions.openFile(proteinId);
        if (success) {
          success();
        }
      },
      always() {
        LoadingScreen.hide();
      },
      tagSchemaId,
      studyIds,
    });
  },

  create({
    name,
    aminoAcids,
    folder,
    translationLink,
    success,
    always,
    tagSchemaId,
    studyIds,
  }: ProteinCreateData & {success?: (proteinId: string) => unknown; always?: () => void}): void {
    // Use `then` instead of `done` to allow chaining callbacks
    ProteinDAO.create({name, aminoAcids, folder, translationLink, tagSchemaId, studyIds})
      .then((response: Protein) => {
        const proteinId = response.id;
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_OPEN_COMPLETED,
          protein: response,
          proteinId: response.id,
        });
        ProteinActions.fetchVersions(proteinId);
        if (success) {
          // Allow the `success` callback to be completed before the `always` callback.
          return success(proteinId);
        } else {
          return null;
        }
      })
      .fail(notifications.notifyError)
      .always(() => {
        if (always) {
          always();
        }
      });
  },

  open(
    proteinId: string,
    {deferred, versionId, view}: {deferred?: JQueryDeferred<unknown>; versionId?: number; view?: boolean}
  ): void {
    ProteinActions.fetchVersions(proteinId);
    ProteinDAO.get(proteinId, versionId, view)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_OPEN_COMPLETED,
          protein: response,
          proteinId,
        });
        if (deferred) {
          deferred.resolve();
        }
      })
      .fail((response) => {
        if (deferred) {
          deferred.reject(response);
        }
      });
  },

  // Updates AA sequences with properties in `data`; fully replaces AA sequence with response from server
  update(proteinId: string, data: ProteinUpdateData, {success}: {success?: () => void} = {}): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.update(proteinId, data, expectedVersionId)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_UPDATE_COMPLETED,
          proteinId,
          newProtein: response,
        });
        if (success) {
          success();
        }
      })
      .fail(handleProteinUpdateError);
  },

  postServerUpdate(proteinId: string, protein: Partial<Protein>): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_MERGE,
      proteinId,
      protein,
    });
  },
  async promptAddAnnotationToFeatureLibrary(proteinId: string, annotationId: string): Promise<void> {
    const annotation = ProteinStore.getAnnotation(proteinId, annotationId)!;
    const aminoAcids = ProteinStore.getAnnotationAminoAcids(proteinId, annotationId);

    const featureLibraryId = await runModal(AddAnnotationToFeatureLibraryModal, {
      annotationResidues: aminoAcids,
    });
    if (featureLibraryId != null) {
      await createFeature({
        name: annotation.name,
        featureType: annotation.type,
        bases: aminoAcids,
        color: annotation.color,
        matchType: MatchType.PROTEIN,
        libraryId: featureLibraryId,
      })
        .done(() => {
          notifications.success('Feature successfully added to library.');
        })
        .fail(notifications.notifyError);
    }
  },

  delete(proteinId: string, {success}: {success?: () => void} = {}): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.delete(proteinId, expectedVersionId)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_DELETE_COMPLETED,
          proteinId,
        });
        if (success) {
          success();
        }
      })
      .fail(handleProteinUpdateError);
  },

  revert(proteinId: string, versionId: number): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.revert(proteinId, versionId, expectedVersionId)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_OPEN_COMPLETED,
          protein: response,
          proteinId,
        });
        ProteinActions.fetchVersions(proteinId);
        notifications.success('Successfully reset to an older version.');
      })
      .fail(handleProteinUpdateError);
  },

  fetchVersions(proteinId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_FETCH_VERSIONS,
      proteinId,
    });
    ProteinDAO.fetchVersions(proteinId)
      .done((response: {data: Array<ProteinVersion>}) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_FETCH_VERSIONS_COMPLETED,
          versions: response.data,
          proteinId,
        });
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_FETCH_VERSIONS_FAILED,
          error: response,
          proteinId,
        });
        notifications.notifyError(response);
      });
  },

  openSearchView(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_SEARCH_OPEN_VIEW,
    });
  },

  completeOpenSearchView(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_SEARCH_OPEN_VIEW_COMPLETED,
    });
  },

  searchAminoAcids(query: string, range?: [number, number]): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_SEARCH_AMINO_ACIDS,
      query,
      range,
    });
  },

  addProteinsForSearch(proteinIds: Array<string>): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_ADD_FOR_SEARCH,
      proteinIds,
    });
  },

  removeProteinsFromSearch(proteinIds: Array<string>): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_REMOVE_FROM_SEARCH,
      proteinIds,
    });
  },

  viewNextSearchResult(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_VIEW_NEXT_SEARCH_RESULT,
    });
  },

  viewPreviousSearchResult(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_VIEW_PREVIOUS_SEARCH_RESULT,
    });
  },

  selectSearchResult(targetDetails: {proteinId: string; index: number; aminoAcids: string}): void {
    AppDispatcher.dispatch({
      ...targetDetails,
      actionType: EventConstants.PROTEIN_SELECT_SEARCH_RESULT,
    });
  },

  clearSearchResults(): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_CLEAR_SEARCH_RESULTS,
    });
  },

  insertAminoAcids(
    proteinId: string,
    {
      position,
      aminoAcids,
      onSuccess,
    }: {
      position: number;
      aminoAcids: string;
      onSuccess?: () => void;
    }
  ): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.insertAminoAcids(proteinId, {position, aminoAcids, expectedVersionId})
      .done((proteinResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_AA_UPDATE_COMPLETED,
          proteinId,
          newProtein: proteinResponse,
        });
        ProteinActions.fetchVersions(proteinId);
        if (onSuccess) {
          onSuccess();
        }
      })
      .fail(handleProteinUpdateError);
  },

  deleteAminoAcids(
    proteinId: string,
    {start, end}: {start: number; end: number},
    onSuccess?: () => void
  ): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.deleteAminoAcids(proteinId, {start, end, expectedVersionId})
      .done((proteinResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_AA_UPDATE_COMPLETED,
          proteinId,
          newProtein: proteinResponse,
        });
        ProteinActions.fetchVersions(proteinId);
        onSuccess?.();
      })
      .fail(handleProteinUpdateError);
  },

  replaceAminoAcids(
    proteinId: string,
    {
      start,
      end,
      aminoAcids,
      onSuccess,
    }: {start: number; end: number; aminoAcids: string; onSuccess?: () => void}
  ): void {
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.replaceAminoAcids(proteinId, {start, end, aminoAcids, expectedVersionId})
      .done((proteinResponse) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_AA_UPDATE_COMPLETED,
          proteinId,
          newProtein: proteinResponse,
        });
        ProteinActions.fetchVersions(proteinId);
        if (onSuccess) {
          onSuccess();
        }
      })
      .fail(handleProteinUpdateError);
  },

  undo(proteinId: string): void {
    const undoVersionId = ProteinStore.getUndoVersionId(proteinId);
    if (undoVersionId != null) {
      ProteinActions.revert(proteinId, undoVersionId);
    }
  },

  autoAnnotate(proteinId: string, featureLibraryIds: Array<number>): void {
    const originalNumAnnotations = ProteinStore.getProteinById(proteinId)!.version.annotations.length;
    const expectedVersionId = ProteinStore.getLatestVersionId(proteinId);
    ProteinDAO.autoAnnotate(proteinId, expectedVersionId, featureLibraryIds)
      .done((proteinResponse: Protein) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTEIN_UPDATE_COMPLETED,
          proteinId,
          newProtein: proteinResponse,
        });
        ProteinActions.fetchVersions(proteinId);
        const numAdded = proteinResponse.version.annotations.length - originalNumAnnotations;

        analytics.track('protein', 'completed_auto_annotate', {
          product: ANALYTICS_MODULE.MOLBIO,
          annotationCount: numAdded,
          protein_id: proteinId,
        });
        if (numAdded === 0) {
          notifications.info('No new features were found.');
        } else {
          notifications.info(`Annotated ${numAdded} ${benchlingUtil.pluralize(numAdded, 'feature')}.`);
        }
      })
      .fail((res) => {
        analytics.track('protein', 'auto_annotate_fail', {
          product: ANALYTICS_MODULE.MOLBIO,
          error: notifications.getFriendlyError(res),
          protein_id: proteinId,
        });
        handleProteinUpdateError(res);
      });
  },

  async bulkAutoAnnotate(proteinIds: Array<string>, featureLibraryIds: Array<number>): Promise<void> {
    const description = `${proteinIds.length} ${benchlingUtil.pluralize(proteinIds.length, 'protein')}`;
    const taskId = TrayTaskActions.createTrayTask({
      heading: `Auto-annotating ${description}`,
    });

    try {
      await ProteinDAO.bulkAutoAnnotate(proteinIds, featureLibraryIds);
      TrayTaskActions.updateTrayTask(taskId, {
        progress: TrayTaskConstants.SUCCESS_PROGRESS,
        heading: `Annotated ${description}`,
      });

      // Refetch AA sequences that are potentially open in the UI
      for (const proteinId of proteinIds) {
        const protein = ProteinStore.getProteinById(proteinId);
        if (protein) {
          // Despite its name, the open method fetches an AA sequence from the server and then fetches its versions
          ProteinActions.open(proteinId, {});
        }
      }
    } catch (e) {
      if (!e.userMessage) {
        TrayTaskActions.removeTrayTask(taskId);
        throw e;
      }

      TrayTaskActions.updateTrayTask(taskId, {
        progress: TrayTaskConstants.FAILURE_PROGRESS,
        error: e.userMessage,
      });
    }
  },

  async bulkRemoveAnnotations(proteinIds: Array<string>): Promise<void> {
    await ProteinDAO.bulkRemoveAnnotations(proteinIds);
    notifications.success(
      `Removed all annotations from ${proteinIds.length} ${benchlingUtil.pluralize(
        proteinIds.length,
        'AA sequence'
      )}`
    );
  },

  dbImportProtein({
    accession,
    database,
    folderId,
  }: {
    accession: string;
    database: string;
    folderId: string;
  }): void {
    const taskId = TrayTaskActions.createTrayTask({
      heading: `Importing ${accession} from ${database}`,
    });
    TrayTaskActions.onProgressFor(taskId)(TrayTaskConstants.INDETERMINATE_PROGRESS);
    ProteinDAO.dbImportProtein({accession, database: database.toLowerCase(), folderId})
      .done((response: {protein: Protein}) => {
        FolderDAOCache.invalidateFolderContents(folderId);
        TrayTaskActions.updateTrayTask(taskId, {
          progress: TrayTaskConstants.SUCCESS_PROGRESS,
          heading: `Imported ${accession} to ${response.protein.folder.name}`,
          onHeadingClick: () => {
            require('FileStates').default.get().openFolderItem(response.protein.id, {open: true});
          },
        });
        analytics.track('protein', 'db_import_succeeded', {
          product: ANALYTICS_MODULE.MOLBIO,
          importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.DB,
          accession,
          database,
        });
      })
      .fail((response) => {
        const friendlyError = notifications.getFriendlyError(response);
        const error = friendlyError || 'Sorry, an unexpected error occurred.';
        TrayTaskActions.updateTrayTask(taskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error,
        });
        analytics.track('protein', 'db_import_failed', {
          product: ANALYTICS_MODULE.MOLBIO,
          importFlow: AA_SEQUENCE_IMPORT_MODAL_TAB.DB,
          accession,
          database,
          error,
        });
      });
    preferences.set(PROTEIN_DEFAULT_DATABASE_IMPORT_PREFERENCE_KEY, database);
  },

  /**
   * Set the currently-being-viewed coordinate structure ID for an AA sequence version.
   * This allows components to track which coordinate structure is currently being viewed.
   */
  setCoordinateStructureId(versionId: number, coordinateStructureId: string | null): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTEIN_SET_CURRENT_COORDINATE_STRUCTURE_ID,
      versionId,
      coordinateStructureId,
    });
  },
};

export default ProteinActions;
